import { API_BASE } from "@/lib/constants";

/**
 * Fire-and-forget analytics event logger.
 * Uses fetch with keepalive so nav-away clicks (like App Store link)
 * still complete the beacon before the page unloads.
 *
 * Also mirrors download_click / google_play_click events into gtag so they
 * flow into Google Analytics 4 (G-LT6YWCW394) and Google Ads
 * (G-P3EWKGCHS5) — enabling conversion tracking on ad campaigns.
 *
 * Never throws — analytics failures must not break UX.
 */
export function trackEvent(event, source, meta) {
  // 1) Own-DB beacon (unchanged)
  try {
    const body = JSON.stringify({ event, source, meta });
    fetch(`${API_BASE}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* swallow — analytics can never break the app */
  }

  // 2) Mirror into gtag (Google Analytics + Google Ads).
  //    Consent Mode gates the underlying request, so this is safe to always call.
  try {
    const g = typeof window !== "undefined" ? window.gtag : undefined;
    if (typeof g === "function") {
      if (event === "download_click") {
        g("event", "download_click", {
          source,
          store: (meta && meta.store) || "apple",
          utm_source: meta && meta.utm_source,
          utm_medium: meta && meta.utm_medium,
          short: meta && meta.short,
        });
      } else if (event === "google_play_click") {
        g("event", "google_play_click", {
          source,
          store: "google_play",
        });
      } else if (event === "share_click") {
        // Referral / social share intent — mirror to GA4 as `share`
        // (GA4 recommended event name for content shares).
        g("event", "share", {
          method: (meta && meta.channel) || source,
          content_type: "app",
          item_id: "stable-ish",
        });
      }
      // gtag config already sends its own page_view; skip to avoid dupes.
    }
  } catch {
    /* gtag failures must never break the app */
  }

  // 3) Mirror into fbq (Meta Pixel). Meta's own consent state gates the
  //    actual send, so this is safe to always call. We use standard events
  //    so Meta Ads Manager understands them out of the box.
  try {
    const fbq = typeof window !== "undefined" ? window.fbq : undefined;
    if (typeof fbq === "function") {
      if (event === "download_click") {
        // "Lead" is Meta's standard event for intent to install / sign up —
        // this is what you'll pick as your conversion goal in Ads Manager.
        fbq("track", "Lead", {
          content_name: "App Store click",
          content_category: source || "unknown",
        });
      } else if (event === "google_play_click") {
        fbq("track", "Lead", {
          content_name: "Google Play click",
          content_category: source || "unknown",
        });
      } else if (event === "share_click") {
        // Standard Meta event for shares — surfaces in Ads Manager as
        // "share" and lets us build lookalike audiences from referrers.
        fbq("trackCustom", "AppShare", {
          channel: (meta && meta.channel) || source || "unknown",
        });
      } else if (event === "page_view") {
        // Meta's inline snippet fires PageView on initial load; mirror
        // client-side route changes here so SPA navigations get counted.
        fbq("track", "PageView");
      }
    }
  } catch {
    /* fbq failures must never break the app */
  }

  // 4) Mirror into ttq (TikTok Pixel). TikTok Pixel consent is gated via
  //    holdConsent()/grantConsent() from CookieBanner, so events queued
  //    before consent are silently dropped by TikTok.
  try {
    const ttq = typeof window !== "undefined" ? window.ttq : undefined;
    if (ttq && typeof ttq.track === "function") {
      if (event === "download_click") {
        // TikTok standard event for CTA click → downstream App Store click.
        // Choose ClickButton in TikTok Ads Manager as the optimization goal.
        ttq.track("ClickButton", {
          content_type: "app",
          content_id: "stable-ish-ios",
          content_name: "App Store click",
          description: source || "unknown",
        });
      } else if (event === "share_click") {
        ttq.track("ClickButton", {
          content_type: "share",
          content_id: "stable-ish-ios",
          content_name: "App share",
          description: (meta && meta.channel) || source || "unknown",
        });
      }
      // TikTok's inline snippet fires ttq.page() on initial load; SPA
      // navigations should fire it again on page_view events.
      if (event === "page_view" && typeof ttq.page === "function") {
        ttq.page();
      }
    }
  } catch {
    /* ttq failures must never break the app */
  }
}

/**
 * Capture referrer + UTM params from the current page, normalized:
 *  - referrer_host: bare hostname of document.referrer with "www." stripped,
 *                   or "direct" if no referrer, or "internal" if same host.
 *  - utm_source / utm_medium / utm_campaign: from URL query params if present.
 *
 * Never throws. Values capped at 60 chars to keep meta small.
 */
function getReferrerInfo() {
  const meta = {};
  try {
    let referrer_host = "direct";
    if (document.referrer) {
      try {
        const url = new URL(document.referrer);
        if (url.hostname) {
          const host = url.hostname.replace(/^www\./, "");
          referrer_host =
            host === window.location.hostname.replace(/^www\./, "")
              ? "internal"
              : host.slice(0, 60);
        }
      } catch {
        /* ignore malformed referrer */
      }
    }
    meta.referrer_host = referrer_host;

    const params = new URLSearchParams(window.location.search);
    ["utm_source", "utm_medium", "utm_campaign"].forEach((k) => {
      const v = params.get(k);
      if (v) meta[k] = v.slice(0, 60);
    });
  } catch {
    /* ignore any DOM access errors */
  }
  return meta;
}

/**
 * Track a page view with referrer + UTM info baked in.
 */
export function trackPageView(source = "landing") {
  trackEvent("page_view", source, getReferrerInfo());
}
